import { DateTime } from 'luxon';
import React, { useCallback } from 'react';
import { useAsyncRetry } from 'react-use';

import {
  Content,
  Page,
  ResponseErrorPanel,
  Table,
  TableColumn,
} from '@backstage/core-components';
import {
  alertApiRef,
  discoveryApiRef,
  identityApiRef,
  useApi,
} from '@backstage/core-plugin-api';

import Button from '@material-ui/core/Button';
import Tooltip from '@material-ui/core/Tooltip';

import { useCanTriggerProviders } from './useCanTriggerProviders';

interface ProviderTask {
  id: string;
  nextRunAt: string | null;
  isRunning: boolean;
}

/**
 * Settings tab that lists every scheduled catalog task with its next-run time
 * and lets permitted users trigger it to run immediately.
 */
export const EntityProvidersSettings = () => {
  const discoveryApi = useApi(discoveryApiRef);
  const identityApi = useApi(identityApiRef);
  const alertApi = useApi(alertApiRef);
  const { canTrigger } = useCanTriggerProviders();

  const {
    value: providers,
    loading,
    error,
    retry,
  } = useAsyncRetry(async (): Promise<ProviderTask[]> => {
    const baseUrl = await discoveryApi.getBaseUrl('utility-api');
    const { token } = await identityApi.getCredentials();
    const response = await fetch(`${baseUrl}/providers`, {
      headers: {
        Accept: 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    });
    if (!response.ok) {
      throw new Error(`Failed to load entity providers (${response.status})`);
    }
    const data = await response.json();
    return data.providers ?? [];
  }, [discoveryApi, identityApi]);

  const triggerProvider = useCallback(
    async (id: string) => {
      try {
        const baseUrl = await discoveryApi.getBaseUrl('utility-api');
        const { token } = await identityApi.getCredentials();
        const response = await fetch(`${baseUrl}/providers/trigger`, {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({ id }),
        });
        if (!response.ok) {
          const body = await response.json().catch(() => ({}));
          // Our own handlers return { error: '<string>' }; the shared error
          // middleware returns { error: { message } }.
          const message =
            typeof body.error === 'string' ? body.error : body.error?.message;
          throw new Error(message ?? `Trigger failed (${response.status})`);
        }
        alertApi.post({
          message: `Triggered ${id} — it will run now`,
          severity: 'success',
          display: 'transient',
        });
        retry();
      } catch (e) {
        alertApi.post({
          message: `Could not trigger ${id}: ${
            e instanceof Error ? e.message : String(e)
          }`,
          severity: 'error',
        });
      }
    },
    [discoveryApi, identityApi, alertApi, retry],
  );

  const columns: TableColumn<ProviderTask>[] = [
    { title: 'Provider', field: 'id' },
    {
      title: 'Next run',
      field: 'nextRunAt',
      render: row =>
        row.nextRunAt
          ? DateTime.fromISO(row.nextRunAt).toLocaleString(
              DateTime.DATETIME_MED_WITH_SECONDS,
            )
          : '—',
    },
    {
      title: 'Actions',
      sorting: false,
      render: row => (
        <Tooltip
          title={
            canTrigger
              ? 'Set the next run to now'
              : 'You do not have permission to trigger providers in this environment'
          }
        >
          <span>
            <Button
              variant="contained"
              color="primary"
              size="small"
              disabled={!canTrigger || row.isRunning}
              onClick={() => triggerProvider(row.id)}
            >
              Trigger
            </Button>
          </span>
        </Tooltip>
      ),
    },
  ];

  return (
    <Page themeId="tool">
      <Content>
        {error ? (
          <ResponseErrorPanel error={error} />
        ) : (
          <Table<ProviderTask>
            title="Entity providers"
            subtitle="Trigger a scheduled provider to run immediately"
            options={{ paging: true, pageSize: 20, search: true }}
            columns={columns}
            data={providers ?? []}
            isLoading={loading}
          />
        )}
      </Content>
    </Page>
  );
};
